import { useEffect, useState } from 'react';
import { venuesApi, generalApi } from '@/lib/api';
import type { Venue, TagCategory } from '@/types/api';
import heroVideo from '@/assets/hero.mp4';

export type HomeHero = {
  title?: string;
  subtitle?: string;
  backgroundUrl?: string;
  videoUrl?: string;
};

export type HomeStats = {
  members?: number;
  venues?: number;
  cities?: number;
};

export type HomeData = {
  hero?: HomeHero;
  featuredVenues?: Venue[];
  stats?: HomeStats;
  categories?: string[];
  venueTags?: TagCategory | null;
};

export function useHome(autoFetch = true) {
  const [data, setData] = useState<HomeData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!autoFetch) return;
    let mounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch data in parallel for better performance
        const [venues, tags, stats] = await Promise.all([
          venuesApi.getVenues(1, 6),
          generalApi.getVenueTags().catch(() => null),
          // generalApi.getStats().catch(() => ({ members: 10000, venues: 0, cities: 50 }))
        ]);

        if (!mounted) return;

        // Extract venue type categories from tags
        let categories = ["All Venues", "Nightclubs", "Restaurants", "Lounges", "Bars"];
        if (tags && tags.venue_type) {
          // Get the first 4-6 most common venue types
          const venueTypes = tags.venue_type
            .map((tag: any) => tag.name)
            .filter((name: string) =>
              ["All Venues", "Nightclub", "Nightclubs", "Restaurant", "Restaurants", "Lounge", "Lounges", "Bar", "Bars", "Club", "Clubs"].includes(name)
            )
            .slice(0, 6);

          if (venueTypes.length > 0) {
            categories = venueTypes;
          }
        }

        // Set the home data with featured venues
        setData({
          hero: {
            title: "No Fees, Just Good Times & Rewards",
            subtitle: "From exclusive nightclubs to fine dining restaurants, find and book the perfect venue for your next night out.",
            videoUrl: heroVideo
          },
          featuredVenues: venues.data || [],
          venueTags: tags,
          categories
        });
      } catch (err) {
        console.error('Error fetching home data:', err);
        if (!mounted) return;
        setError(err as Error);
        // Set fallback data on error
        setData({
          hero: {
            title: "No Fees, Just Good Times & Rewards",
            subtitle: "From exclusive nightclubs to fine dining restaurants, find and book the perfect venue for your next night out.",
            videoUrl: heroVideo
          },
          featuredVenues: [],
          stats: {
            members: 10000,
            venues: 0,
            cities: 50
          },
          categories: ["Nightclubs", "Restaurants", "Lounges", "Bars"]
        });
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    // Ask for the visitor's location; if allowed, swap in venues near them.
    // Denied / unavailable / nothing nearby → keep the default list.
    const fetchNearby = () => {
      navigator.geolocation?.getCurrentPosition(async ({ coords }) => {
        // Widen the box (~55km → ~1700km) until something turns up.
        // ponytail: up to 4 requests, no distance sort; add a server-side distance sort if this gets slow
        for (const d of [0.5, 2, 5, 15]) {
          const bounds = [coords.latitude - d, coords.latitude + d, coords.longitude - d, coords.longitude + d].join(',');
          try {
            const nearby = await venuesApi.getVenues(1, 6, bounds);
            if (!mounted) return;
            if (nearby.data?.length) {
              setData(prev => prev ? { ...prev, featuredVenues: nearby.data } : prev);
              return;
            }
          } catch { return; /* keep default venues */ }
        }
      }, () => {}, { timeout: 10000, maximumAge: 600000 });
    };

    fetchData().then(fetchNearby);
    return () => { mounted = false; };
  }, [autoFetch]);

  return { data, isLoading, error };
}
